import {
  HHAlert,
  HHCheckbox,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useHingeSelectClient } from '../../api';
import { StatusBadge } from '../components/badges/status-badge';
import { SkeletonTableRows } from '../components/feedback/loading-skeleton';
import { DisabledInfo, StepNav } from '../components/layout/step-nav';
import {
  fadeInKeyframes,
  MOTION_DURATION,
  MOTION_EASING,
  usePrefersReducedMotion,
} from '../components/motion/motion';
import { ContractInput, ContractInputLocation } from '../context/types';
import { useStepHeader } from '../context/wizard-header.context';
import { useWizardState } from '../context/wizard-state.context';
import { formatAddressOneLine } from '../utils/format-address';
import { distinctTins, getOrgGroups } from '../utils/org-groups';

const monoText = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 13,
};

const locationKey = (loc: ContractInputLocation): string =>
  `${loc.address.locality}|${loc.address.address_line_1}`;

interface LocationsStepInnerProps {
  input: ContractInput;
}

const LocationsStepInner = ({
  input,
}: LocationsStepInnerProps): JSX.Element => {
  const { state, setState } = useWizardState();
  const client = useHingeSelectClient();
  const reduced = usePrefersReducedMotion();
  const fired = useRef(false);
  const firedList = useRef(false);
  const [search, setSearch] = useState('');
  const [tinFilter, setTinFilter] = useState<string | 'all'>('all');
  const [existingErrors, setExistingErrors] = useState<
    Record<string, string | undefined>
  >({});

  useStepHeader({
    description:
      'Pick the locations this contract covers. Top table: rows from the contract input — checked by default. Bottom table per merchant: locations already on file — opt them in if the contract also covers them. Rows shown in both lists lock to the top-table choice.',
  });

  const locations = input.locations;
  const tins = useMemo(() => distinctTins(input), [input]);
  const orgGroups = useMemo(() => getOrgGroups(input), [input]);
  const isMultiTin = tins.length > 1;

  const filteredLocations = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return locations.filter(loc => {
      if (tinFilter !== 'all' && loc.tax_id !== tinFilter) return false;
      if (needle === '') return true;
      const haystack =
        `${loc.address.locality} ${loc.address.address_line_1} ${loc.address.address_line_2 ?? ''} ${loc.address.postal_code} ${loc.group_npi_type2}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [locations, search, tinFilter]);

  // Auto point-lookup per contract row. Kept for two reasons: (a) tells us
  // whether each input row is `new` vs `existing` so commit can pick the right
  // write, and (b) gives us a merchantLocationId per existing input row that
  // we cross-reference against the existing-on-file list to lock overlapping
  // bottom-table rows.
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    void (async (): Promise<void> => {
      for (const loc of locations) {
        const key = locationKey(loc);
        if (state.resolutions.merchantLocations[key] != null) continue;
        const tin = loc.tax_id;
        const npi2 = loc.group_npi_type2;
        try {
          const result = await client.resolveMerchantLocation({ tin, npi2 });
          const claimedMlId = result.ok
            ? (result.data.merchantLocation.id ?? '')
            : '';
          setState(s => {
            // Locations step is the single source of truth for selection: when
            // an input row resolves to an existing MerchantLocation, strip that
            // mlId from the `existing` selection so downstream consumers
            // (CSPs, commit) never see the same location twice.
            const nextExisting =
              claimedMlId !== '' &&
              claimedMlId in s.decisions.locationSelection.existing
                ? Object.fromEntries(
                    Object.entries(
                      s.decisions.locationSelection.existing,
                    ).filter(([mlId]) => mlId !== claimedMlId),
                  )
                : s.decisions.locationSelection.existing;
            return {
              ...s,
              resolutions: {
                ...s.resolutions,
                merchantLocations: {
                  ...s.resolutions.merchantLocations,
                  [key]: result.ok
                    ? {
                        status: 'existing',
                        data: {
                          merchantLocationId: claimedMlId,
                          name: result.data.merchantLocation.name,
                          displayName: result.data.merchantLocation.displayName,
                        },
                      }
                    : { status: 'new', reason: result.reason },
                },
              },
              decisions: {
                ...s.decisions,
                locationSelection: {
                  ...s.decisions.locationSelection,
                  existing: nextExisting,
                },
              },
            };
          });
        } catch (e) {
          setState(s => ({
            ...s,
            resolutions: {
              ...s.resolutions,
              merchantLocations: {
                ...s.resolutions.merchantLocations,
                [key]: {
                  status: 'conflict',
                  conflictFields: [
                    {
                      field: 'network',
                      incoming: npi2,
                      existing: (e as Error).message,
                    },
                  ],
                },
              },
            },
          }));
        }
      }
    })();
  }, [client, locations, setState, state.resolutions.merchantLocations]);

  // Fetch the existing-on-file MerchantLocation list once per TIN whose
  // Merchant resolved with an id. TINs whose merchant is brand-new have
  // nothing to list and are skipped.
  useEffect(() => {
    if (firedList.current) return;
    firedList.current = true;
    void (async (): Promise<void> => {
      for (const g of orgGroups) {
        const tin = g.tax_id;
        const merchantId = state.resolutions.merchant[tin]?.data?.merchantId;
        if (merchantId == null || merchantId === '') continue;
        if (state.resolutions.existingMerchantLocations[tin] != null) continue;
        try {
          const result = await client.listMerchantLocationsForMerchant({
            merchantId,
          });
          setState(s => ({
            ...s,
            resolutions: {
              ...s.resolutions,
              existingMerchantLocations: {
                ...s.resolutions.existingMerchantLocations,
                [tin]: result.merchantLocations,
              },
            },
          }));
        } catch (e) {
          setExistingErrors(prev => ({ ...prev, [tin]: (e as Error).message }));
        }
      }
    })();
  }, [
    client,
    orgGroups,
    setState,
    state.resolutions.merchant,
    state.resolutions.existingMerchantLocations,
  ]);

  // IDs claimed by an input row that auto-resolved to an existing MerchantLocation.
  // Bottom-table rows whose id is in this set are forced-on and disabled in the UI
  // (and skipped at commit time so the link step doesn't run twice).
  const claimedById = useMemo(() => {
    const set = new Set<string>();
    for (const loc of locations) {
      const r = state.resolutions.merchantLocations[locationKey(loc)];
      const id = r?.data?.merchantLocationId;
      if (r?.status === 'existing' && id != null && id !== '') {
        set.add(id);
      }
    }
    return set;
  }, [locations, state.resolutions.merchantLocations]);

  const isInputChecked = (key: string): boolean =>
    state.decisions.locationSelection.input[key] ?? true;

  const isExistingChecked = (mlId: string): boolean =>
    state.decisions.locationSelection.existing[mlId] ?? false;

  const setInputChecked = (key: string, v: boolean): void => {
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        locationSelection: {
          ...s.decisions.locationSelection,
          input: { ...s.decisions.locationSelection.input, [key]: v },
        },
      },
    }));
  };

  const setExistingCheckedFor = (mlId: string, v: boolean): void => {
    // Defense-in-depth: refuse to record an existing mlId that's already
    // claimed by an input row. The UI disables these checkboxes, but if a
    // future caller wires this differently, the invariant still holds.
    if (v === true && claimedById.has(mlId)) return;
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        locationSelection: {
          ...s.decisions.locationSelection,
          existing: { ...s.decisions.locationSelection.existing, [mlId]: v },
        },
      },
    }));
  };

  const getLocationName = (key: string, loc: ContractInputLocation): string =>
    state.decisions.locationNames[key] ?? loc.address.locality;

  const setLocationName = (key: string, name: string): void => {
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        locationNames: { ...s.decisions.locationNames, [key]: name },
      },
    }));
  };

  // Continue rules:
  //  - At least one location selected somewhere (an input row checked, or an
  //    existing row checked, or any overlap rows — those count automatically).
  //  - Every CHECKED input row has a clean resolution (`existing` or `new`).
  //    Unchecked rows are ignored; pending/conflict on checked rows blocks.
  const checkedInputRows = locations.filter(loc =>
    isInputChecked(locationKey(loc)),
  );
  const allCheckedResolved = checkedInputRows.every(loc => {
    const r = state.resolutions.merchantLocations[locationKey(loc)];
    return r != null && (r.status === 'existing' || r.status === 'new');
  });
  const checkedExistingIds = Object.entries(
    state.decisions.locationSelection.existing,
  )
    .filter(([, v]) => v)
    .map(([id]) => id);
  const anySelected =
    checkedInputRows.length > 0 || checkedExistingIds.length > 0;

  const canContinue = anySelected && allCheckedResolved;
  const disabledInfo: DisabledInfo | undefined = canContinue
    ? undefined
    : !anySelected
      ? {
          message: 'Select at least one location to continue.',
          severity: 'info',
        }
      : {
          message:
            'Some checked input rows are still resolving or have conflicts. Wait for them to settle (or uncheck them) before continuing.',
          severity: 'warning',
        };

  return (
    <HHStack spacing={3}>
      <HHStack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flexWrap: 'wrap', gap: 1 }}
      >
        <HHTextField
          hhVariant="variant-bypass"
          size="small"
          placeholder="Search city, address, NPI-2, or ZIP…"
          value={search}
          onChange={(e): void => setSearch(e.target.value)}
          sx={{ minWidth: 320 }}
        />
        {isMultiTin && (
          <HHStack direction="row" spacing={0.75} alignItems="center">
            <Chip
              label={`All (${locations.length})`}
              size="small"
              color={tinFilter === 'all' ? 'primary' : 'default'}
              variant={tinFilter === 'all' ? 'filled' : 'outlined'}
              onClick={(): void => setTinFilter('all')}
            />
            {tins.map(tin => {
              const count = locations.filter(l => l.tax_id === tin).length;
              const active = tinFilter === tin;
              return (
                <Chip
                  key={tin}
                  label={`TIN ${tin} (${count})`}
                  size="small"
                  color={active ? 'primary' : 'default'}
                  variant={active ? 'filled' : 'outlined'}
                  onClick={(): void => setTinFilter(tin)}
                />
              );
            })}
          </HHStack>
        )}
        <span style={{ flex: 1 }} />
        <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
          Showing {filteredLocations.length} of {locations.length}
        </HHTypography>
      </HHStack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}>Include</TableCell>
              <TableCell sx={{ width: 180 }}>Name</TableCell>
              <TableCell>Address</TableCell>
              {isMultiTin && <TableCell sx={{ width: 130 }}>TIN</TableCell>}
              <TableCell sx={{ width: 130 }}>NPI-2</TableCell>
              <TableCell sx={{ width: 220 }}>Status</TableCell>
              <TableCell sx={{ width: 220 }}>Location ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLocations.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isMultiTin ? 7 : 6}
                  sx={{ textAlign: 'center', opacity: 0.6, py: 3 }}
                >
                  No locations match the current filter.
                </TableCell>
              </TableRow>
            ) : (
              filteredLocations.map(loc => {
                const key = locationKey(loc);
                const r = state.resolutions.merchantLocations[key];
                const status = r?.status ?? 'pending';
                const checked = isInputChecked(key);
                return (
                  <TableRow key={key} hover selected={checked}>
                    <TableCell>
                      <HHCheckbox
                        hhVariant="variant-bypass"
                        checked={checked}
                        onChange={(_, v): void => setInputChecked(key, v)}
                      />
                    </TableCell>
                    <TableCell>
                      {status === 'new' ? (
                        <HHTextField
                          hhVariant="variant-bypass"
                          size="small"
                          value={getLocationName(key, loc)}
                          onChange={(e): void =>
                            setLocationName(key, e.target.value)
                          }
                          sx={{ width: '100%' }}
                          inputProps={{ style: { fontSize: 13 } }}
                        />
                      ) : status === 'existing' ? (
                        <HHTypography hhVariant="body2">
                          {r?.data?.displayName ??
                            r?.data?.name ??
                            loc.address.locality}
                        </HHTypography>
                      ) : (
                        loc.address.locality
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {formatAddressOneLine(loc.address)}
                    </TableCell>
                    {isMultiTin && (
                      <TableCell sx={monoText}>{loc.tax_id}</TableCell>
                    )}
                    <TableCell sx={monoText}>{loc.group_npi_type2}</TableCell>
                    <TableCell>
                      <StatusBadge status={status} />
                    </TableCell>
                    <TableCell sx={monoText}>
                      {r?.data?.merchantLocationId ?? '—'}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {orgGroups
        .filter(g => tinFilter === 'all' || g.tax_id === tinFilter)
        .map(g => {
          const tin = g.tax_id;
          const merchantRes = state.resolutions.merchant[tin];
          const merchantId = merchantRes?.data?.merchantId;
          const merchantName =
            state.decisions.orgNames[tin] ?? input.entity_legal_name;
          const list = state.resolutions.existingMerchantLocations[tin];
          const err = existingErrors[tin];

          let body: JSX.Element;
          if (merchantId == null || merchantId === '') {
            body = (
              <HHTypography
                hhVariant="body2"
                sx={{ opacity: 0.7, p: 2, textAlign: 'center' }}
              >
                Merchant is new — no existing locations on file.
              </HHTypography>
            );
          } else if (err != null) {
            body = (
              <HHAlert hhVariant="outlined" severity="warning">
                Failed to load existing locations for this merchant: {err}.
                Continue with the input rows above; you can rerun this step
                after fixing.
              </HHAlert>
            );
          } else if (list == null) {
            body = (
              <Table size="small">
                <TableBody>
                  <SkeletonTableRows rows={3} columns={5} />
                </TableBody>
              </Table>
            );
          } else if (list.length === 0) {
            body = (
              <HHTypography
                hhVariant="body2"
                sx={{ opacity: 0.7, p: 2, textAlign: 'center' }}
              >
                No locations on file for this merchant.
              </HHTypography>
            );
          } else {
            body = (
              <Table
                size="small"
                sx={{
                  animation: reduced
                    ? 'none'
                    : `${fadeInKeyframes} ${MOTION_DURATION.state}ms ${MOTION_EASING.standard} both`,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 60 }}>Include</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell sx={{ width: 130 }}>NPI-2</TableCell>
                    <TableCell sx={{ width: 220 }}>
                      MerchantLocation ID
                    </TableCell>
                    <TableCell sx={{ width: 180 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map(ml => {
                    const id = ml.id ?? '';
                    const overlap = id !== '' && claimedById.has(id);
                    const checked = overlap || isExistingChecked(id);
                    return (
                      <TableRow key={id || ml.displayName || ml.npi2}>
                        <TableCell>
                          <HHCheckbox
                            hhVariant="variant-bypass"
                            checked={checked}
                            disabled={overlap || id === ''}
                            onChange={(_, v): void =>
                              setExistingCheckedFor(id, v)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {ml.displayName ?? ml.name ?? '—'}
                        </TableCell>
                        <TableCell sx={monoText}>{ml.npi2 ?? '—'}</TableCell>
                        <TableCell sx={monoText}>{id || '—'}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>
                          {overlap && (
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label="Matched from input"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            );
          }

          return (
            <Paper key={tin} variant="outlined">
              <HHStack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                }}
              >
                <HHTypography hhVariant="subtitle2">
                  Existing locations on file for {merchantName}
                  {isMultiTin ? ` · TIN ${tin}` : ''}
                  {list != null ? ` (${list.length})` : ''}
                </HHTypography>
                <span style={{ flex: 1 }} />
                <HHTypography hhVariant="caption" sx={{ opacity: 0.6 }}>
                  Check rows the contract also covers. Locked rows are already
                  represented above.
                </HHTypography>
              </HHStack>
              {body}
            </Paper>
          );
        })}

      <StepNav
        id="locations"
        canContinue={canContinue}
        disabledInfo={disabledInfo}
      />
    </HHStack>
  );
};

export const LocationsStep = (): JSX.Element => {
  const { state } = useWizardState();
  if (state.input == null) return <Navigate to="../intake" replace />;
  return <LocationsStepInner input={state.input} />;
};
